/**
 * KsefConfigPage — Główny entry point konfiguracji KSeF (Krok 4 - integracja).
 *
 * Decyduje co pokazać na podstawie stanu backendu:
 *   - Jeśli (!has_cert || !company_nip) → renderuj <KsefConfigWizard />
 *   - W przeciwnym razie → renderuj <KsefConfigTabs />
 *
 * Wizard po zakończeniu (Krok 4 → "Zakończ") wywołuje callback onFinished:
 *   → refetch stanu → flaga is_complete = true → wrapper automatycznie
 *     przeskakuje na tryb Tabsów (bez refresha strony).
 *
 * Tabsy po kliknięciu "Usuń konfigurację" automatycznie wracają do wizarda
 * (bo adminConfig.has_cert znika).
 */
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";

import { getAdminConfig, getSavedNip } from "./services/ksefApi";
import { KsefConfigWizard } from "./KsefConfigWizard";
import { KsefConfigTabs } from "./KsefConfigTabs";

export const KsefConfigPage = () => {
  const [adminConfig, setAdminConfig] = useState(null);
  const [savedNip, setSavedNip] = useState("");
  const [loading, setLoading] = useState(true);
  // Force-flip do tabsów po zakończeniu wizarda (zanim refetch zwróci nowe dane).
  // Po refetchu adminConfig.has_cert i savedNip będą true, wtedy ten override staje się zbędny.
  const [forceTabs, setForceTabs] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgRes, nipRes] = await Promise.all([
        getAdminConfig().catch(() => ({ data: null })),
        getSavedNip().catch(() => ({ data: null })),
      ]);
      setAdminConfig(cfgRes.data || null);
      setSavedNip(nipRes.data?.company_nip || cfgRes.data?.company_nip || "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleWizardFinished = useCallback(async () => {
    setForceTabs(true);
    await fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <Card className="border border-gray-200">
        <CardContent className="flex items-center justify-center py-16">
          <div
            className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"
            data-testid="ksef-config-loading"
          />
        </CardContent>
      </Card>
    );
  }

  // Decyzja: czy pokazać wizard, czy tabsy
  const isConfigured = !!adminConfig?.has_cert && !!savedNip;
  const showTabs = forceTabs || isConfigured;

  return showTabs ? (
    <KsefConfigTabs />
  ) : (
    <KsefConfigWizard onFinished={handleWizardFinished} />
  );
};

export default KsefConfigPage;
